import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

// Function to create a custom colored icon for pins
const createColoredIcon = (color) => {
  const iconUrl =
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30">
          <circle cx="15" cy="15" r="12" stroke="white" stroke-width="2" fill="${color}" />
        </svg>
      `);
  return new L.Icon({
    iconUrl: iconUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
};

const DrawControl = ({ pins, setPins, handlePinUpdate }) => {
  const map = useMap(); // Using useMap hook to get map instance

  // Use refs to avoid re-attaching event listeners when state or callbacks change
  const pinsRef = React.useRef(pins);
  const setPinsRef = React.useRef(setPins);
  const handlePinUpdateRef = React.useRef(handlePinUpdate);

  // Sync refs with latest props
  useEffect(() => {
    pinsRef.current = pins;
    setPinsRef.current = setPins;
    handlePinUpdateRef.current = handlePinUpdate;
  }, [pins, setPins, handlePinUpdate]);

  useEffect(() => {
    const drawControl = new L.Control.Draw({
      draw: {
        polyline: false,
        polygon: false,
        circle: false,
        marker: false,
        circlemarker: false,
        rectangle: {
          shapeOptions: {
            color: "#3388ff", // Rectangle color
          },
          showArea: false, // Hide area information
        },
      },
    });
    L.drawLocal.draw.toolbar.buttons.rectangle = "Draw"; // Set button text

    // Add draw control to the map
    map.addControl(drawControl);

    const onCreate = (event) => {
      const { layer } = event;
      const bounds = layer.getBounds();

      // Update pins
      const updatedPins = pinsRef.current.map((pin) => {
        const lat = parseFloat(pin.koordinat_x);
        const lng = parseFloat(pin.koordinat_y);
        if (isNaN(lat) || isNaN(lng)) {
          return { ...pin, isSelected: false };
        }
        const pinLatLng = L.latLng(lat, lng);
        const isSelected = bounds.contains(pinLatLng); // Check if pin is inside the rectangle
        return {
          ...pin,
          isSelected, // Update selection status
          color:
            isSelected && (pin.color === "red" || pin.color === "yellow")
              ? pin.color === "red"
                ? "yellow"
                : "red"
              : pin.color, // Change color if selected
        };
      });

      if (handlePinUpdateRef.current) handlePinUpdateRef.current(updatedPins);
      if (setPinsRef.current) setPinsRef.current(updatedPins); // Update all pins
    };

    const onDelete = () => {
      // Unselect all pins when drawing is deleted
      const updatedPins = pinsRef.current.map((pin) => ({
        ...pin,
        isSelected: false,
        color: "red",
      }));
      if (setPinsRef.current) setPinsRef.current(updatedPins);
    };

    map.on("draw:created", onCreate);
    map.on("draw:deleted", onDelete);

    return () => {
      map.removeControl(drawControl); // Cleanup on unmount
      map.off("draw:created", onCreate);
      map.off("draw:deleted", onDelete);
    };
  }, [map]);

  return null;
};

const Map = ({
  lat = -5.1167,
  lng = 105.2475,
  initialPins = [],
  handlePinUpdate,
}) => {
  const [userLocation, setUserLocation] = useState(null); // State for user location
  const [selectedPin, setSelectedPin] = useState(null);
  const [pins, setPins] = useState(initialPins); // State for all pins

  // Custom green circle icon for user's current location
  const greenCircleIcon = createColoredIcon("orange");

  useEffect(() => {
    // Get user's current location
    setPins(initialPins);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  }, [initialPins]);

  const handlePinSelect = (pin) => {
    setSelectedPin(pin);
  };

  return (
    <div className="map-container" style={{ height: "500px" }}>
      <MapContainer
        center={userLocation || [lat, lng]} // Use userLocation or fallback to props lat/lng
        zoom={13}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {/* Add DrawControl component to manage rectangle drawing */}
        <DrawControl
          pins={pins}
          setPins={setPins}
          handlePinUpdate={handlePinUpdate}
        />

        {/* Display all pins dynamically */}
        {pins.map((pin, id) => {
          const lat = parseFloat(pin.koordinat_x);
          const lng = parseFloat(pin.koordinat_y);
          if (isNaN(lat) || isNaN(lng)) return null;
          return (
            <Marker
              key={id}
              position={[lat, lng]}
              icon={createColoredIcon(pin.color)} // Use custom color for each pin
              eventHandlers={{
                click: () => {
                  handlePinSelect(pin);
                },
              }}
            >
              <Popup>
                <strong>{pin.nama}</strong>
                <br />
                Latitude: {pin.koordinat_x}
                <br />
                Longitude: {pin.koordinat_y}
              </Popup>
            </Marker>
          );
        })}

        {/* If user location is available, show a marker for their current position */}
        {userLocation && (
          <Marker
            position={[userLocation.lat, userLocation.lng]}
            icon={greenCircleIcon} // Green circle for user's location
          >
            <Popup>Your Current Location</Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Display details of the selected pin */}
      {selectedPin && (
        <div style={{ marginTop: "20px" }}>
          <h3>Selected Pin:</h3>
          <p>ID: {selectedPin.id}</p>
          <p>Name: {selectedPin.nama}</p>
          <p>Latitude: {selectedPin.koordinat_x}</p>
          <p>Longitude: {selectedPin.koordinat_y}</p>
        </div>
      )}
    </div>
  );
};

export default Map;
